import { useState, useRef, useEffect } from "react"
import { select } from 'd3-selection'
import { bin, median } from 'd3-array'
import { area, curveBumpY } from 'd3-shape'
import { axisLeft } from 'd3-axis'
import { scaleLinear } from 'd3-scale'
import { zoom, zoomIdentity } from 'd3-zoom'
import { useFetchGtex } from '../hooks.js'

const colors = {
    "Adipose_Subcutaneous": "#FFB27F",
    "Adipose_Visceral": "#FFD47F",
    "Adrenal_Gland": "#99EE99",
    "Artery_Aorta": "#FFAAAA",
    "Artery_Coronary": "#FFD4CC",
    "Artery_Tibial": "#FF7F7F",
    "Bladder": "#D47F7F",
    "Brain_Amygdala": "#F6F67F",
    "Brain_Anterior_cingulate_cortex": "#F6F67F",
    "Brain_Caudate": "#F6F67F",
    "Brain_Cerebellar_Hemisphere": "#F6F67F",
    "Brain_Cerebellum": "#F6F67F",
    "Brain_Cortex": "#F6F67F",
    "Brain_Frontal_Cortex": "#F6F67F",
    "Brain_Hippocampus": "#F6F67F",
    "Brain_Hypothalamus": "#F6F67F",
    "Brain_Nucleus_accumbens": "#F6F67F",
    "Brain_Putamen": "#F6F67F",
    "Brain_Spinal_cord": "#F6F67F",
    "Brain_Substantia_nigra": "#F6F67F",
    "Breast_Mammary_Tissue": "#99E5E5",
    "Cells_Cultured_fibroblasts": "#D4F6FF",
    "Cells_EBV_transformed_lymphocytes": "#E5B2FF",
    "Cervix_Ectocervix": "#FFE5E5",
    "Cervix_Endocervix": "#E5D4EE",
    "Colon_Sigmoid": "#F6DDBB",
    "Colon_Transverse": "#E5CCAA",
    "Esophagus_Gastroesophageal_Junction": "#C5B9AA",
    "Esophagus_Mucosa": "#AA907F",
    "Esophagus_Muscularis": "#DDCCC3",
    "Fallopian_Tube": "#FFE5E5",
    "Heart_Atrial_Appendage": "#CC7FFF",
    "Heart_Left_Ventricle": "#99FFE0",
    "Kidney_Cortex": "#90FFEE",
    "Kidney_Medulla": "#99FFE0",
    "Liver": "#D4DDB2",
    "Lung": "#CCFF7F",
    "Minor_Salivary_Gland": "#CCDDC3",
    "Muscle_Skeletal": "#D4D4FF",
    "Nerve_Tibial": "#FFEB7F",
    "Ovary": "#FFD4FF",
    "Pancreas": "#CCAA90",
    "Pituitary": "#D4FFCC",
    "Prostate": "#EEEEEE",
    "Skin_Not_Sun_Exposed": "#7F7FFF",
    "Skin_Sun_Exposed": "#BBBBFF",
    "Small_Intestine_Terminal_Ileum": "#AAAA90",
    "Spleen": "#BBC3AA",
    "Stomach": "#FFEECC",
    "Testis": "#D4D4D4",
    "Thyroid": "#7FB27F",
    "Uterus": "#FFB2FF",
    "Vagina": "#FFAACC",
    "Whole_Blood": "#FF7FDD"
    }

const margin = {top: 52, right: 20, bottom: 130, left: 62}
const height = 480
const band_width = 28

/*
*
* titleFunc: accession => title
*
* Renders a GTEx violin plot of TPM across tissues. The figure always fits
* inside its container (smaller if many tissues) and can be explored like a
* magnifying glass: scroll to zoom (proportional, x-axis only), drag to pan
* across all tissues, double-click to reset.
* */
const paintSvg = (svg, {gtex, accession}, titleFunc) => {

    svg.selectAll('*').remove()

    const svgEl = svg.node()
    const parent = svgEl && svgEl.parentElement
    const padX = parent
        ? (parseFloat(getComputedStyle(parent).paddingLeft) || 0) + (parseFloat(getComputedStyle(parent).paddingRight) || 0)
        : 0
    const containerW = parent ? Math.max(320, parent.clientWidth - padX) : 800

    const tissues = Object.entries(gtex || {})
    const plotH = height - margin.top - margin.bottom

    if (!tissues.length) {
        svg.attr("width", containerW).attr("height", height)
        svg.append("text")
            .attr("x", margin.left)
            .attr("y", height / 2)
            .style("font-size", "13px")
            .style("fill", "#94a3b8")
            .text("No GTEx expression data for this transcript")
        return
    }

    const contentW = tissues.length * band_width
    const viewPlotW = Math.max(50, containerW - margin.left - margin.right)
    const baseK = Math.min(1, viewPlotW / contentW)
    const svgW = baseK < 1 ? containerW : contentW + margin.left + margin.right

    svg.attr("width", svgW).attr("height", height)
    svgEl.style.maxWidth = "none"
    svgEl.style.display = "block"

    const xPos = idx => idx * band_width + band_width / 2

    const maxTpm = Math.max(1, ...tissues.map(([, d]) => d.length ? Math.max(...d) : 0))

    // Title + subtitle
    svg.append("text")
        .attr("x", margin.left).attr("y", 28)
        .style("font-size", "15px").style("font-weight", "600").style("fill", "#0f172a")
        .text(titleFunc ? titleFunc(accession) : accession)
    svg.append("text")
        .attr("x", margin.left).attr("y", 45)
        .style("font-size", "12px").style("fill", "#64748b")
        .text("RNA expression (TPM) across tissues — GTEx")

    // Y axis label
    svg.append("text")
        .attr("x", 12).attr("y", margin.top + plotH / 2)
        .attr("transform", `rotate(-90 12 ${margin.top + plotH / 2})`)
        .attr("text-anchor", "middle")
        .style("font-size", "11px").style("fill", "#64748b")
        .text("TPM")

    // inner: scaled plot (violins, medians, x baseline). overlay: crisp gridlines, axis and labels.
    const inner = svg.append("g")
    const overlay = svg.append("g")

    const getViolin = (data, idx, y) => {
        const wScale = scaleLinear()
            .domain([0, data.length])
            .range([idx * band_width + 1, idx * band_width + band_width - 1])
        const areaBuilder = area()
            .x0((d) => wScale(-d.length))
            .x1((d) => wScale(d.length))
            .y((d) => y(d.x0))
            .curve(curveBumpY)
        return areaBuilder(bin().thresholds(14)(data))
    }

    const renderAt = (k, txRaw) => {
        txRaw = txRaw || 0
        const lo = Math.min(0, viewPlotW - contentW * k)
        const tx = Math.max(lo, Math.min(0, txRaw))

        inner.attr("transform", `translate(${margin.left + tx}, ${margin.top}) scale(${k}, 1)`)

        // Rescale the y-domain so zooming magnifies the figure proportionally.
        const kR = k / baseK
        const yNow = scaleLinear()
            .domain([0, maxTpm / Math.max(1, kR)])
            .range([plotH, 0])
            .nice()

        // Violins + medians
        inner.selectAll(".violin")
            .data(tissues)
            .join("path")
            .attr("class", "violin")
            .attr("d", ([, d], idx) => getViolin(d, idx, yNow))
            .attr("fill", ([t]) => colors[t] || "#cbd5e1")
            .attr("opacity", 0.9)
            .attr("stroke", "#0f172a")
            .attr("stroke-opacity", 0.15)
            .attr("stroke-width", 0.5)
            .each(function ([t, d]) {
                const md = median(d)
                if (select(this).select("title").empty()) {
                    select(this).append("title")
                        .text(`${t}\nmedian ${Number.isFinite(md) ? Number(md).toFixed(2) : "n/a"} TPM · ${d.length} samples`)
                }
            })

        inner.selectAll(".median")
            .data(tissues)
            .join("line")
            .attr("class", "median")
            .attr("x1", ([, d], idx) => idx * band_width + 2)
            .attr("x2", ([, d], idx) => (idx + 1) * band_width - 2)
            .attr("y1", ([, d]) => yNow(median(d)))
            .attr("y2", ([, d]) => yNow(median(d)))
            .attr("stroke", "#0f172a")
            .attr("stroke-width", 1.4)

        // X axis baseline (anchored to the bottom of the plot, stays fixed)
        inner.selectAll(".baseline")
            .data([0])
            .join("line")
            .attr("class", "baseline")
            .attr("x1", 0).attr("x2", contentW)
            .attr("y1", plotH).attr("y2", plotH)
            .attr("stroke", "#94a3b8").attr("stroke-width", 1)

        // Overlay: crisp gridlines, y-axis numbers and tissue labels (never scaled)
        overlay.selectAll("*").remove()

        overlay.append("g")
            .attr("transform", `translate(${margin.left + tx},${margin.top})`)
            .call(axisLeft(yNow).ticks(6).tickSize(-contentW * k).tickFormat(""))
        overlay.selectAll("g .tick line").attr("stroke", "#e2e8f0")
        overlay.select("path.domain").attr("stroke", "none")

        overlay.append("g")
            .attr("transform", `translate(${margin.left - 40},${margin.top})`)
            .call(axisLeft(yNow).ticks(6).tickSize(0))
        overlay.selectAll("g text").style("font-size", "11px").style("fill", "#475569")

        overlay.selectAll(".tissue-label")
            .data(tissues)
            .join("text")
            .attr("class", "tissue-label")
            .attr("x", ([, d], idx) => margin.left + tx + xPos(idx) * k)
            .attr("y", margin.top + plotH + 14)
            .attr("transform", ([, d], idx) =>
                `rotate(-55 ${margin.left + tx + xPos(idx) * k} ${margin.top + plotH + 14})`)
            .style("font-size", "10.5px")
            .style("text-anchor", "end")
            .style("fill", "#334155")
            .text(([t]) => t)
    }

    // X-only zoom/pan: proportional magnification, full pan across tissues.
    const zoomBehavior = zoom()
        .scaleExtent([baseK, baseK * 10])
        .extent([[0, 0], [svgW, plotH]])
        .translateExtent([[-1e6, -1e6], [1e6, 1e6]])
        .on("zoom", (event) => renderAt(event.transform.k, event.transform.x))

    svg.on("dblclick.zoom", null)
    svg.on("dblclick", () => svg.call(zoomBehavior.transform, zoomIdentity.scale(baseK)))

    svg.call(zoomBehavior)
    svg.call(zoomBehavior.transform, zoomIdentity.scale(baseK))
}

const TranscriptExpression = ({transcript}) => {

    const { setSVGNode } = useFetchGtex({transcript, paintSvg})
    
    const svgRef = useRef()

    useEffect(() => {
        const svg = select(svgRef.current)
        setSVGNode(svg)
    }, [])

    return (
        <div className="mt-5">
            <div className="w-full rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <svg ref={svgRef} className="block cursor-grab" width="760" height={height} />
            </div>
            <p className="mt-1.5 text-xs text-slate-400">
                Drag to pan · scroll to zoom in/out · double-click to reset
            </p>
        </div>
    );
}

export default TranscriptExpression